/* photo-to-gif/index.js — DOM orchestrator for "images (and GIFs) → one GIF".
 *
 * Frames come from decode.js, the encoded sequence from sequence.js, encoding
 * from the shared gif-frames/encode.js that /draw-animation also uses. This
 * module owns the frame strip, the preview and the controls.
 *
 * Dropping GIF files in is not a separate "merge" feature with its own screen:
 * an animated GIF simply contributes all of its frames, so two GIFs in gives one
 * GIF out. Same code path, same controls.
 *
 * TWO RULES THIS FILE EXISTS TO KEEP:
 *   · The preview plays the SAME sequence the encoder receives — direction,
 *     crossfade, fit and background all go through buildSequence(), so "what
 *     you watched" cannot drift from "what you downloaded".
 *   · The finished file gets a place ON THE PAGE. A GIF that only lands in the
 *     downloads folder leaves no way to check the weight, no way to save it
 *     again, and no way to tell whether the settings were right.
 */

import { decodeFile, suggestSize, supported } from './decode.js';
import { buildSequence, expandedCount, fadeStepsFor, naturalCompare } from './sequence.js';
import { estimateGifBytes } from './estimate.js';
import { createCard, NO_FRAME } from './card.js';
import { buildMotionSequence, isAnimated, DEFAULT_MOTION, MOTION_DELAY_MS } from './motion.js';
import { encodeGif, encodeWebm, gifRepeatForPlays } from '../gif-frames/encode.js';
import { encodeFastGif } from '../greeting-card/gif-fast.js';
import { downloadBlob } from '../toy-fx/canvas-export.js';
import { initFunnel, emit } from '../toy-fx/telemetry.js';

const SLUG = 'photo-to-gif';
const I18N = (typeof window !== 'undefined' && window.PhotoToGifI18n) || {};
const t = (k, f) => (I18N && I18N[k]) || f || k;
const $ = (id) => document.getElementById(id);

const MAX_FRAMES = 300;
/* Ceiling on ENCODED frames (originals + crossfade in-betweens). gif.js holds
 * every frame's pixels in memory at once, so 640×568 × 600 frames is already
 * ~870 MB inside the encoder — past this the tab does not finish, it dies. */
const MAX_ENCODED = 600;
const THUMB_W = 108;
const HEAVY_BYTES = 10 * 1024 * 1024;

/* Frames are {canvas, delay, from}. `delay` starts from the global speed and is
 * overridable per frame — a title card wants to sit longer than the frames
 * around it, and that is the single most-asked-for control on a slideshow GIF. */
let frames = [];
let out = { width: 480, height: 360 };
let seq = [];
let playing = false;
let playTimer = null;
let playIndex = 0;
let busy = false;
let partialWarned = false;
let direction = 'forward';
let background = '#ffffff';
let lockRatio = true;
let exportAbort = null;
let estAbort = null;
let estTimer = null;
let resultUrl = null;
/* Frame + captions over the animation. Inactive until the user picks something,
 * and while inactive it costs exactly one `if`. */
const card = createCard((typeof window !== 'undefined' && window.__LOCALE) || 'en');
let galleryCat = 'all';
let gallerySrc = null;
/* Отпечатки ИСХОДНЫХ файлов, а не текущих кадров. Единица суточного капа здесь —
 * ВЫДАННАЯ анимация, а не кадр: из трёхсот кадров получается один файл. Ключ
 * строится по набору того, что пользователь загрузил, поэтому переставить кадры,
 * удалить лишний, поправить длительности и переэкспортировать (хоть в GIF, хоть
 * в WebM) — бесплатно: это работа над ОДНИМ проектом. Новый слот стоит только
 * новый набор исходников. Сбрасывается вместе с очисткой. */
let sourceKeys = [];
/* One photo → motion (motion.js). The choice survives a photo swap on purpose:
 * someone animating a series of single photos keeps "snow, slow" for each. */
let motion = DEFAULT_MOTION;
let overlay = 'none';
let motionSpeed = 'normal';
let laneSeen = false;
/* The motion GIF is encoded whole to measure its weight (cheap: one frame in
 * memory, ~0.3–0.8 s on a desktop for 38 frames). The export reuses that file
 * when nothing changed in between, so «Скачать» is instant. */
let motionDone = null;   // {seq, key, blob}
/* A GIF already left this page today. With the day's budget spent, a DIFFERENT
 * set of photos is a new unit and meets the wall — live 20.10: 5 sessions in
 * 9 hours went «one photo → export → add a photo → wall». Said up front, under
 * the zone that invites adding (Farid 21.10: warn, do not change the policy). */
let exportedHere = false;
let capNoteSeen = false;

function ae(f, extra) { emit(SLUG, f, extra); }
function stage(name) { try { if (window.__stage) window.__stage(name); } catch (_) {} }
function announce(msg) { try { window.__announce && window.__announce(msg); } catch (_) {} }

/* ── settings readers ──────────────────────────────────────────────────── */

function globalDelay() {
    const fps = parseFloat(($('ptg-fps') || {}).value || '2');
    return Math.max(20, Math.round(1000 / Math.max(0.1, fps)));
}
function colors() { return parseInt(($('ptg-colors') || {}).value || '128', 10) || 128; }
function fitMode() { return ($('ptg-fit') || {}).value || 'contain'; }
function loopValue() { return parseInt(($('ptg-loop') || {}).value || '0', 10); }
function looping() { return loopValue() === 0; }
function dithering() { const el = $('ptg-dither'); return el ? el.checked : false; }
function globalPalette() { const el = $('ptg-palette'); return el ? el.checked : false; }
function fadeMs() { return parseInt(($('ptg-fade') || {}).value || '0', 10) || 0; }

/* ONE photo is its own case: there is nothing to flip through, so the speed,
 * direction and crossfade controls do nothing to it — and 29 % of the people
 * who exported a single frame had tried them first. The motion block replaces
 * them. A frame of an animated GIF is not a "photo" here (decode.js `still`). */
function motionLane() { return frames.length === 1 && frames[0].still === true; }
function animatedStill() { return motionLane() && isAnimated(motion, overlay); }
function reducedMotion() {
    try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (_) { return false; }
}
function motionKey() {
    return [out.width, out.height, colors(), dithering() ? 1 : 0, loopValue()].join('|');
}

/* Step count per fade. Aimed at ~70 ms a step so a fade looks continuous
 * without turning five photos into three hundred frames. */
function wantedFadeSteps() {
    const ms = fadeMs();
    if (!ms) return 0;
    return Math.max(2, Math.min(10, Math.round(ms / 70)));
}
function effectiveFade() {
    return fadeStepsFor(frames.length, wantedFadeSteps(), {
        direction, looping: looping(), ceiling: MAX_ENCODED,
    });
}

/* ── formatting ────────────────────────────────────────────────────────── */

function nf(v, digits) {
    try {
        return new Intl.NumberFormat(window.__LOCALE || undefined, {
            minimumFractionDigits: digits, maximumFractionDigits: digits,
        }).format(v);
    } catch (_) { return v.toFixed(digits); }
}

function fmtBytes(b) {
    if (!b) return '—';
    if (b < 1024 * 1024) return nf(Math.round(b / 1024), 0) + ' ' + t('unit_kb', 'KB');
    return nf(b / (1024 * 1024), 1) + ' ' + t('unit_mb', 'MB');
}

function fmtSeconds(ms) { return nf(ms / 1000, 1) + ' ' + t('sec_short', 's'); }

/* Trailing zeros dropped: "0.5" instead of "0.50". A number input is only ~40 px
 * wide on a frame card, and the two characters are the difference between the
 * value fitting and being cut off. Kept as a plain "." decimal — that is what
 * <input type=number> requires; the browser localises what it displays. */
function durText(ms) { return String(+((ms / 1000).toFixed(2))); }

/* ── sequence + stats ──────────────────────────────────────────────────── */

function rebuildSequence() {
    if (!frames.length) { seq = []; return; }
    const { steps } = effectiveFade();
    /* The animation is built at the size of the frame's WINDOW, not the whole
     * canvas. Pre-fitting to the window is what keeps "fit" and "background"
     * meaningful under a frame: by the time doc.js places the photo it is
     * already the right shape, so its own cover-fit crops nothing. With no
     * frame the window IS the canvas and this is the old behaviour exactly. */
    const win = card.windowFor(out.width, out.height);
    const base = animatedStill()
        ? buildMotionSequence(frames[0], {
            width: win.w,
            height: win.h,
            fit: fitMode(),
            background,
            motion,
            overlay,
            speed: motionSpeed,
        })
        : buildSequence(frames, {
            width: win.w,
            height: win.h,
            fit: fitMode(),
            background,
            direction,
            fadeMs: fadeMs(),
            fadeSteps: steps,
            looping: looping(),
        });
    seq = card.decorate(base, out.width, out.height);
}

function renderStats() {
    const { steps, clamped } = effectiveFade();
    const total = seq.length;
    const durMs = seq.reduce((s, i) => s + i.delay, 0);

    const nEl = $('ptg-stat-frames');
    if (nEl) {
        nEl.textContent = (steps > 0 || animatedStill()) && total !== frames.length
            ? t('frames_expanded', ':a → :b').replace(':a', frames.length).replace(':b', total)
            : String(frames.length);
    }
    const dEl = $('ptg-stat-dur');
    if (dEl) dEl.textContent = fmtSeconds(durMs);
    const sEl = $('ptg-stat-size');
    if (sEl) sEl.textContent = out.width + '×' + out.height;

    const fadeNote = $('ptg-fade-note');
    if (fadeNote && clamped) {
        fadeNote.textContent = t('fade_clamped', 'Fade shortened to stay under the frame ceiling.');
    } else if (fadeNote) {
        fadeNote.textContent = fadeNote.dataset.base || fadeNote.textContent;
    }

    const scrub = $('ptg-scrub');
    if (scrub) {
        scrub.max = String(Math.max(0, total - 1));
        scrub.disabled = total < 2;
        if (parseInt(scrub.value, 10) > total - 1) scrub.value = '0';
    }
    renderPos();

    const w = $('ptg-width');
    const h = $('ptg-height');
    if (w && document.activeElement !== w) w.value = String(out.width);
    if (h && document.activeElement !== h) h.value = String(out.height);
}

function renderPos() {
    const el = $('ptg-pos');
    if (!el) return;
    if (!seq.length) { el.textContent = ''; return; }
    const i = playing ? playIndex : parseInt(($('ptg-scrub') || {}).value || '0', 10);
    el.textContent = t('pos_line', ':i / :n').replace(':i', (i % seq.length) + 1).replace(':n', seq.length);
}

/* ── size estimate ─────────────────────────────────────────────────────── */

function scheduleEstimate() {
    if (estTimer) clearTimeout(estTimer);
    if (estAbort) { estAbort.abort(); estAbort = null; }

    const cell = $('ptg-stat-weight');
    const li = $('ptg-stat-weight-li');
    const heavy = $('ptg-heavy-note');
    if (li) li.classList.remove('is-heavy');
    if (heavy) heavy.classList.add('hidden');

    if (!seq.length || busy) { if (cell) cell.textContent = '—'; return; }
    if (cell) cell.textContent = t('est_pending', '…');

    /* Debounced: someone nudging the width field would otherwise start an
     * encode per keystroke. */
    estTimer = setTimeout(() => {
        const ctrl = new AbortController();
        estAbort = ctrl;
        const stale = () => ctrl.signal.aborted || estAbort !== ctrl;

        /* Fonts first here too: a caption rendered in the fallback face weighs
         * differently from the real one, so estimating before the face lands
         * answers about a picture the user is not going to get. */
        const lane = animatedStill();
        const forSeq = seq;
        const key = motionKey();
        card.ensureFonts()
            .catch(() => {})
            .then(() => {
                if (stale()) return null;
                /* Motion GIFs keep only the changed pixels per frame, so a few
                 * spread-out sample frames say nothing about the file: encode
                 * it whole (one frame in memory at a time) and keep the result
                 * for the export. */
                if (lane) {
                    return encodeMotionGif(forSeq, ctrl.signal, null).then((blob) => {
                        if (stale()) return null;
                        motionDone = { seq: forSeq, key, blob };
                        return { bytes: blob.size, total: forSeq.length };
                    });
                }
                return estimateGifBytes(seq, {
                    width: out.width, height: out.height, colors: colors(),
                    dither: dithering(), globalPalette: globalPalette(), background,
                }, ctrl.signal);
            })
            .then((res) => {
                if (!res || stale()) return;
                if (cell) cell.textContent = t('est_approx', '≈ :v').replace(':v', fmtBytes(res.bytes));
                const isHeavy = res.bytes > HEAVY_BYTES;
                if (li) li.classList.toggle('is-heavy', isHeavy);
                if (heavy) heavy.classList.toggle('hidden', !isHeavy);
                ae('size_estimated', { bytes: res.bytes, frames: res.total });
            })
            .catch(() => {
                if (estAbort === ctrl && cell) cell.textContent = '—';
            });
    }, 700);
}

/* ── rendering ─────────────────────────────────────────────────────────── */

function renderAll({ list = true } = {}) {
    if (list) renderList();
    rebuildSequence();
    renderStats();
    renderPreview(playing ? playIndex : parseInt(($('ptg-scrub') || {}).value || '0', 10));
    scheduleEstimate();

    const has = frames.length > 0;
    ['ptg-export-gif', 'ptg-export-webm', 'ptg-clear', 'ptg-sort'].forEach((id) => {
        const el = $(id);
        if (el) el.disabled = !has || busy;
    });
    const play = $('ptg-play');
    if (play) play.disabled = seq.length < 2 || busy;
    syncLaneUi();
    syncCapNote();

    const empty = $('ptg-empty');
    if (empty) empty.classList.toggle('hidden', has);
    const st = $('ptg-stage');
    if (st) st.classList.toggle('hidden', !has);
    const fr = $('ptg-frames');
    if (fr) fr.classList.toggle('hidden', !has);
    const cardBox = $('ptg-card');
    if (cardBox) cardBox.classList.toggle('hidden', !has);
    if (has) renderGallery();

    /* A decorated frame spends palette entries the photo used to have to
     * itself, so 64 colours under a confetti border bands badly. Said only
     * when it applies. */
    const palHint = $('ptg-card-palette-hint');
    if (palHint) palHint.hidden = !(has && card.frameId() !== NO_FRAME && colors() < 256);

    /* Shrink the drop zone once there is something to work on, and relabel it:
     * it stays the way to add photo #6, but it stops being the biggest thing
     * on the screen. */
    const wrap = $('ptg-dropwrap');
    if (wrap) {
        wrap.classList.toggle('is-compact', has);
        const title = wrap.querySelector('.tu-title');
        if (title) title.textContent = has ? t('drop_more', 'Add more frames') : t('drop_title', 'Drop photos here');
    }
}

/* Show the motion block for one photo and hide what cannot act on one frame.
 * Hidden rather than disabled: a greyed "crossfade" next to a single photo
 * still reads as "there is something here to set". */
function syncLaneUi() {
    const lane = motionLane();
    const moving = animatedStill();
    const show = (id, on) => { const el = $(id); if (el) el.classList.toggle('hidden', !on); };
    show('ptg-motion', lane);
    show('ptg-row-speed', !lane);
    show('ptg-row-dir', !lane);
    show('ptg-dir', !lane);
    show('ptg-grp-transition', !lane);
    /* The motion encoder always uses one palette for the whole loop. */
    show('ptg-row-palette', !moving);
    show('ptg-motion-still-note', lane && !moving);
    show('ptg-motion-bg-note', moving && !background);
    if (lane && !laneSeen) {
        laneSeen = true;
        ae('motion_lane_shown', { motion, overlay });
    }
}

function syncCapNote() {
    const el = $('ptg-cap-note');
    if (!el) return;
    let show = false;
    try {
        const L = window.__toolLimit;
        show = !!(exportedHere && frames.length && L && !L.isPro() && L.remaining(SLUG) <= 0);
    } catch (_) {}
    el.classList.toggle('hidden', !show);
    if (show && !capNoteSeen) {
        capNoteSeen = true;
        ae('cap_note_shown', { frames: frames.length });
    }
}

/* Thumbnails are cached ON THE FRAME. Reordering rebuilds the strip, and
 * redrawing 300 thumbnails on every move is the difference between instant and
 * a visible stall. */
function thumbFor(f) {
    if (f.thumb) return f.thumb;
    const cv = document.createElement('canvas');
    cv.width = THUMB_W;
    cv.height = Math.max(1, Math.round(THUMB_W * (f.canvas.height / f.canvas.width)));
    cv.getContext('2d').drawImage(f.canvas, 0, 0, cv.width, cv.height);
    f.thumb = cv;
    return cv;
}

function renderList() {
    const list = $('ptg-list');
    if (!list) return;
    list.textContent = '';
    frames.forEach((f, i) => list.appendChild(buildCard(f, i)));
    const title = $('ptg-frames-title');
    if (title) title.textContent = t('frames_title', 'Frames') + ' · ' + frames.length;
}

function iconBtn(cls, label, path, onClick) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = cls;
    b.setAttribute('aria-label', label);
    b.title = label;
    b.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" '
        + 'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + path + '</svg>';
    b.addEventListener('click', onClick);
    return b;
}

function buildCard(f, i) {
    const card = document.createElement('li');
    card.className = 'ptg-card';
    card.dataset.i = String(i);
    card.setAttribute('aria-label', t('frame_aria', 'Frame :n').replace(':n', i + 1));

    const thumbBox = document.createElement('div');
    thumbBox.className = 'ptg-card-thumb';
    thumbBox.appendChild(thumbFor(f));

    const n = document.createElement('span');
    n.className = 'ptg-card-n';
    n.textContent = String(i + 1);
    thumbBox.appendChild(n);

    const ops = document.createElement('div');
    ops.className = 'ptg-card-ops';
    ops.appendChild(iconBtn('ptg-op ptg-op-dup', t('duplicate_frame', 'Duplicate frame :n').replace(':n', i + 1),
        '<rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 012-2h10"/>', () => {
            if (frames.length >= MAX_FRAMES) { showMaxHint(); return; }
            const copy = Object.assign({}, f, { thumb: null });
            frames.splice(i + 1, 0, copy);
            ae('frame_duplicate', { n: frames.length });
            stopPlayback();
            renderAll();
        }));
    ops.appendChild(iconBtn('ptg-op ptg-op-del', t('remove_frame', 'Remove frame :n').replace(':n', i + 1),
        '<path d="M18 6L6 18"/><path d="M6 6l12 12"/>', () => {
            frames.splice(i, 1);
            ae('frame_remove', { n: frames.length });
            stopPlayback();
            renderAll();
        }));
    thumbBox.appendChild(ops);
    card.appendChild(thumbBox);

    const foot = document.createElement('div');
    foot.className = 'ptg-card-foot';

    /* Explicit move buttons are not a fallback for drag — they are the ONLY
     * reordering that works on a touchscreen (HTML5 drag never fires there)
     * and the only one reachable from a keyboard. */
    const left = iconBtn('ptg-move', t('move_left', 'Move frame :n earlier').replace(':n', i + 1),
        '<path d="M15 18l-6-6 6-6"/>', () => move(i, i - 1));
    left.disabled = i === 0;
    foot.appendChild(left);

    const dur = document.createElement('input');
    dur.type = 'number';
    dur.className = 'ptg-dur';
    dur.min = '0.02';
    dur.max = '10';
    dur.step = '0.05';
    dur.value = durText(f.delay);
    dur.setAttribute('aria-label', t('frame_seconds', 'Seconds for frame :n').replace(':n', i + 1));
    dur.addEventListener('change', () => {
        const v = Math.max(0.02, Math.min(10, parseFloat(dur.value) || 0.5));
        f.delay = Math.round(v * 1000);
        f.pinned = true;      /* a hand-set duration survives a speed change */
        dur.value = durText(f.delay);
        ae('frame_duration_set', { i, ms: f.delay });
        renderAll({ list: false });
    });
    foot.appendChild(dur);

    const right = iconBtn('ptg-move', t('move_right', 'Move frame :n later').replace(':n', i + 1),
        '<path d="M9 18l6-6-6-6"/>', () => move(i, i + 1));
    right.disabled = i === frames.length - 1;
    foot.appendChild(right);

    card.appendChild(foot);
    wireDrag(card, thumbBox, i);
    return card;
}

/* ── greeting card ─────────────────────────────────────────────────────── */

const CAT_LABELS = (typeof window !== 'undefined' && window.PhotoToGifCatLabels) || {};
const catLabel = (id) => CAT_LABELS[id] || id;

function buildCats() {
    const box = $('ptg-card-cats');
    if (!box || box.children.length) return;
    card.categories().forEach((c) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ptg-cat';
        b.dataset.cat = c.id;
        b.setAttribute('role', 'tab');
        b.setAttribute('aria-selected', c.id === galleryCat ? 'true' : 'false');
        b.textContent = catLabel(c.id);
        box.appendChild(b);
    });
    box.addEventListener('click', (e) => {
        const b = e.target.closest ? e.target.closest('[data-cat]') : null;
        if (!b) return;
        galleryCat = b.dataset.cat;
        Array.prototype.forEach.call(box.querySelectorAll('[data-cat]'), (x) => {
            x.setAttribute('aria-selected', x === b ? 'true' : 'false');
        });
        gallerySrc = null;          // force a redraw for the new category
        renderGallery();
        ae('card_cat', { cat: galleryCat });
    });
}

/* Thumbnails are drawn with the USER'S first photo inside each frame, so the
 * choice is made on the real picture rather than on a stock swatch. Rebuilt
 * only when that photo (or the category) actually changed — otherwise every
 * reorder would redraw 39 frames. */
function renderGallery() {
    const box = $('ptg-card-gallery');
    if (!box) return;
    const src = frames.length ? thumbFor(frames[0]) : null;
    const key = (src ? src.toDataURL().slice(0, 64) : 'none') + '|' + galleryCat;
    if (key === gallerySrc) { markFrameSelection(); return; }
    gallerySrc = key;

    box.textContent = '';
    const list = card.catalogue().filter((f) => galleryCat === 'all' || f.cat === galleryCat);
    list.forEach((f) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ptg-fr';
        b.dataset.frame = f.id;
        b.setAttribute('role', 'option');
        const label = f.id === NO_FRAME ? t('card_none', 'No frame') : catLabel(f.cat);
        /* Name only for assistive tech, never printed under the thumbnail:
         * every frame in a category carries the SAME occasion name, so a
         * visible caption repeated seven times down the strip is noise — and
         * "День рождения" overflowed its 84 px cell anyway. /photo-frame's own
         * gallery labels the same way. */
        b.setAttribute('aria-label', t('card_frame_aria', 'Frame :name').replace(':name', label));
        b.appendChild(card.thumbnail(f.id, src, 96, 80));
        box.appendChild(b);
    });
    markFrameSelection();
}

function markFrameSelection() {
    const box = $('ptg-card-gallery');
    if (!box) return;
    Array.prototype.forEach.call(box.querySelectorAll('[data-frame]'), (b) => {
        b.setAttribute('aria-selected', b.dataset.frame === card.frameId() ? 'true' : 'false');
    });
}

/* Every path that PAINTS text must wait for its face first: canvas does not
 * trigger @font-face loading, so an unawaited render silently rasterises the
 * fallback and the export ships the wrong typeface. */
function afterFonts(fn) {
    card.ensureFonts().then(fn, fn);
}

function move(from, to) {
    if (to < 0 || to >= frames.length || from === to) return;
    const [m] = frames.splice(from, 1);
    frames.splice(to, 0, m);
    ae('frame_reorder', { from, to });
    stopPlayback();
    renderAll();
    announce(t('frame_aria', 'Frame :n').replace(':n', to + 1));
    /* Keep the keyboard on the button that was just used, at its new position. */
    const card = $('ptg-list') && $('ptg-list').children[to];
    if (card) {
        const btn = card.querySelector(to > from ? '.ptg-move:last-of-type' : '.ptg-move');
        if (btn && !btn.disabled) btn.focus();
    }
}

/* ── pointer drag (mouse / pen) ────────────────────────────────────────── */

let drag = null;

function wireDrag(card, handle, index) {
    handle.addEventListener('pointerdown', (e) => {
        /* The × and ⧉ buttons sit INSIDE the drag handle, so a press on them
         * arrives here first. Bailing out is not cosmetic: preventDefault() on
         * pointerdown suppresses the compatibility mouse events, `click`
         * included, so without this both buttons are dead on a mouse. */
        if (e.target && e.target.closest && e.target.closest('button')) return;
        /* Touch is deliberately excluded: capturing a finger here would take
         * over the gesture the user needs to SCROLL the strip. Touch reorders
         * with the move buttons, which are always visible for that reason. */
        if (e.pointerType === 'touch') return;
        if (e.button !== 0) return;
        e.preventDefault();
        drag = { from: index, card, moved: false, target: index, after: false };
        card.classList.add('is-dragging');
        try { handle.setPointerCapture(e.pointerId); } catch (_) {}
    });

    handle.addEventListener('pointermove', (e) => {
        if (!drag || drag.card !== card) return;
        drag.moved = true;
        const el = document.elementFromPoint(e.clientX, e.clientY);
        const over = el && el.closest ? el.closest('.ptg-card') : null;
        clearDropMarks();
        if (!over || over === card) { drag.target = drag.from; return; }
        const r = over.getBoundingClientRect();
        const after = e.clientX > r.left + r.width / 2;
        over.classList.add(after ? 'is-drop-after' : 'is-drop-before');
        drag.target = parseInt(over.dataset.i, 10);
        drag.after = after;
    });

    const finish = () => {
        if (!drag || drag.card !== card) return;
        card.classList.remove('is-dragging');
        clearDropMarks();
        const { from, target, after, moved } = drag;
        drag = null;
        if (!moved || target === from) return;
        let to = target + (after ? 1 : 0);
        if (to > from) to--;
        move(from, Math.max(0, Math.min(frames.length - 1, to)));
    };
    handle.addEventListener('pointerup', finish);
    handle.addEventListener('pointercancel', finish);
}

function clearDropMarks() {
    const list = $('ptg-list');
    if (!list) return;
    Array.prototype.forEach.call(list.querySelectorAll('.is-drop-before,.is-drop-after'), (n) => {
        n.classList.remove('is-drop-before', 'is-drop-after');
    });
}

/* ── preview ───────────────────────────────────────────────────────────── */

function renderPreview(i) {
    const cv = $('ptg-preview');
    if (!cv || !seq.length) return;
    if (cv.width !== out.width || cv.height !== out.height) {
        cv.width = out.width;
        cv.height = out.height;
    }
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, out.width, out.height);
    const item = seq[((i % seq.length) + seq.length) % seq.length];
    if (!item) return;
    ctx.drawImage(typeof item.source === 'function' ? item.source() : item.source, 0, 0);
}

function showMaxHint() {
    const hint = $('ptg-max-hint');
    if (hint) hint.classList.remove('hidden');
}

/* The encoder refused up front: the sequence would need more RAM than this
   device gives (Sentry H8 — a 3 GB clone into gif.js's worker). Say how much,
   and which two knobs bring it back inside the budget. */
function showRamHint(err) {
    const hint = $('ptg-ram-hint');
    if (!hint) return;
    hint.textContent = t('ram_hint', 'A GIF this size needs about :need MB of memory. Keep at most :frames frames or reduce the size to :side px.')
        .replace(':need', err.needMB).replace(':frames', err.maxFrames).replace(':side', err.maxSide);
    hint.classList.remove('hidden');
}

/* ── input ─────────────────────────────────────────────────────────────── */

function addFiles(files) {
    const list = Array.from(files || []).filter(supported);
    if (!list.length) {
        try { window.__toast && window.__toast(t('err_unsupported', 'Those files are not images'), 'error'); } catch (_) {}
        return;
    }

    stage('files_added');
    try {
        const fk = window.__toolLimit && window.__toolLimit.fileKey;
        if (fk) {
            for (const f of list) {
                const k = fk.call(window.__toolLimit, f);
                if (k && sourceKeys.indexOf(k) === -1) sourceKeys.push(k);
            }
        }
    } catch (_) {}
    setBusy(true, t('reading', 'Reading files…'));

    /* Sequential decode: an animated GIF holds decoder memory while it runs, and
     * ten of them at once is how the decoder starts refusing frames. */
    let chain = Promise.resolve();
    let anyPartial = false;
    const before = frames.length;
    const wasLane = motionLane();

    list.forEach((file) => {
        chain = chain.then(() => decodeFile(file, globalDelay()).then((res) => {
            if (res.partial) anyPartial = true;
            for (const f of res.frames) {
                if (frames.length >= MAX_FRAMES) break;
                frames.push(f);
            }
        }).catch(() => {
            try { window.__toast && window.__toast(t('err_one_file', 'Could not read :name').replace(':name', file.name), 'error'); } catch (_) {}
        }));
    });

    chain.then(() => {
        if (!before && frames.length) out = suggestSize(frames);
        if (anyPartial && !partialWarned) {
            /* Say it plainly rather than dropping frames in silence. */
            partialWarned = true;
            const msg = t('warn_first_frame_only', 'This browser can only read the first frame of an animated GIF, so it was added as a single frame. Chrome or Edge reads all frames.');
            const note = $('ptg-note');
            if (note) { note.textContent = msg; note.classList.remove('hidden'); }
            try { window.__toast && window.__toast(msg, 'warning'); } catch (_) {}
            ae('gif_flattened', {});
        }
        if (frames.length >= MAX_FRAMES) showMaxHint();
        ae('frames_loaded', { added: frames.length - before, total: frames.length });
        setBusy(false);
        /* Leaving the one-photo lane (a second photo arrived) changes what the
         * preview plays; a running playback would keep indexing the old loop. */
        if (wasLane !== motionLane()) stopPlayback();
        renderAll();
        /* One photo: show it MOVING straight away — a still preview of a GIF
         * that will move is the same misunderstanding the lane exists to end. */
        if (!before && animatedStill() && !reducedMotion()) startPlayback();
    });
}

/* ── playback ──────────────────────────────────────────────────────────── */

function startPlayback() {
    if (playing || seq.length < 2) return;
    playing = true;
    playIndex = parseInt(($('ptg-scrub') || {}).value || '0', 10) || 0;
    setPlayLabel(true);
    ae('preview_play', { frames: seq.length });
    const tick = () => {
        if (!playing) return;
        renderPreview(playIndex);
        const scrub = $('ptg-scrub');
        if (scrub) scrub.value = String(playIndex % seq.length);
        renderPos();
        const hold = seq[playIndex % seq.length].delay;
        playIndex++;
        if (playIndex >= seq.length) {
            if (!looping()) { stopPlayback(); return; }
            playIndex = 0;
        }
        playTimer = setTimeout(tick, hold);
    };
    tick();
}

function stopPlayback() {
    if (!playing) return;
    playing = false;
    if (playTimer) clearTimeout(playTimer);
    playTimer = null;
    setPlayLabel(false);
    renderPos();
}

function setPlayLabel(on) {
    const btn = $('ptg-play');
    if (!btn) return;
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    const label = on ? t('stop', 'Stop') : t('play', 'Preview');
    btn.setAttribute('aria-label', label);
    btn.title = label;
    const p = btn.querySelector('.ptg-ico-play');
    const s = btn.querySelector('.ptg-ico-stop');
    if (p) p.classList.toggle('hidden', on);
    if (s) s.classList.toggle('hidden', !on);
}

/* ── export ────────────────────────────────────────────────────────────── */

function setBusy(on, label) {
    busy = on;
    const bar = $('ptg-progress');
    const note = $('ptg-busy');
    if (bar) bar.classList.toggle('hidden', !on);
    if (note) {
        note.classList.toggle('hidden', !on);
        if (label) note.textContent = label;
    }
    const cancel = $('ptg-cancel');
    if (cancel) cancel.classList.toggle('hidden', !on || !exportAbort);
    ['ptg-export-gif', 'ptg-export-webm', 'ptg-play', 'ptg-sort', 'ptg-clear'].forEach((id) => {
        const el = $(id);
        if (el) el.disabled = on || !frames.length;
    });
}

/* Ключ проекта: отсортированный набор исходников. Сортировка обязательна —
 * иначе те же файлы, добавленные в другом порядке, читались бы как другой
 * проект и стоили бы второй слот. */
function runKey() {
    if (!sourceKeys.length) return null;
    return 'ptg:' + sourceKeys.slice().sort().join('~').slice(0, 400);
}

function showResult(blob, format, frameCount, durMs) {
    const box = $('ptg-result');
    const media = $('ptg-result-media');
    const facts = $('ptg-result-facts');
    if (!box || !media || !facts) return;

    if (resultUrl) { try { URL.revokeObjectURL(resultUrl); } catch (_) {} }
    resultUrl = URL.createObjectURL(blob);

    media.textContent = '';
    if (format === 'webm') {
        const v = document.createElement('video');
        v.src = resultUrl;
        v.autoplay = true; v.loop = true; v.muted = true; v.playsInline = true;
        v.setAttribute('aria-label', t('result_webm', 'WebM result'));
        media.appendChild(v);
    } else {
        const img = document.createElement('img');
        img.src = resultUrl;
        img.alt = t('result_gif', 'GIF result');
        media.appendChild(img);
    }

    facts.textContent = '';
    [
        format === 'webm' ? 'WebM' : 'GIF',
        out.width + '×' + out.height,
        t('result_frames', 'Frames: :n').replace(':n', frameCount),
        fmtSeconds(durMs),
        fmtBytes(blob.size),
    ].forEach((txt) => {
        const li = document.createElement('li');
        li.textContent = txt;
        facts.appendChild(li);
    });

    const dl = $('ptg-result-dl');
    if (dl) dl.onclick = () => {
        save(blob, format);
        ae('result_redownload', { fmt: format });
    };
    box.classList.remove('hidden');
    /* block:'nearest' only — the result sits right under the button that was
     * just pressed, so this nudges it into view without yanking the page. */
    try { box.scrollIntoView({ block: 'nearest' }); } catch (_) {}
}

function save(blob, format) {
    const name = format === 'webm' ? 'animation.webm' : 'animation.gif';
    if (typeof window.__downloadFile === 'function') window.__downloadFile(blob, name);
    else downloadBlob(blob, name);
}

/* One photo in motion → GIF through the greeting cards' encoder: one palette for
 * the loop and only the changed pixels per frame. Measured on three photos at
 * 640 px, 38 frames: snow / sparkles / hearts 0.15–0.3 MB in 0.2–0.5 s, where
 * gif.js wrote 5–8 MB in 2–10 s; a push-in 2.2–3.8 MB. Ordered dithering
 * (the «Сглаживание» box) keeps a sky from banding without making the still
 * parts of the picture shimmer. */
function encodeMotionGif(items, signal, onProgress) {
    const W = out.width, H = out.height;
    return encodeFastGif({
        width: W,
        height: H,
        frames: items.length,
        delay: MOTION_DELAY_MS,
        colors: colors(),
        dither: dithering(),
        repeat: gifRepeatForPlays(loopValue()),
        signal,
        onProgress,
        render: (i, g) => {
            const it = items[i];
            g.clearRect(0, 0, W, H);
            g.drawImage(typeof it.source === 'function' ? it.source() : it.source, 0, 0);
        },
    });
}

function doExport(format) {
    if (busy || !frames.length) return;

    /* Кап списывается ЗА ВЫДАННЫЙ ФАЙЛ. Резерв берём здесь, а закрываем только
     * после того, как файл реально ушёл пользователю: провал кодирования — это
     * не покупка, и суточную норму он стоить не должен. */
    const token = window.__toolLimit ? window.__toolLimit.begin(SLUG, runKey()) : null;
    if (window.__toolLimit && !token) return;   // стена уже показана примитивом

    stopPlayback();
    if (estTimer) clearTimeout(estTimer);
    if (estAbort) { estAbort.abort(); estAbort = null; }
    stage('export_start');

    /* Caption faces before pixels. Canvas silently falls back to a default
     * typeface for a face that has not finished loading, and an export is the
     * one place where that is unrecoverable — the file is already saved. */
    setBusy(true, t('encoding', 'Building the GIF…'));
    card.ensureFonts().then(
        () => runExport(format, token),
        () => runExport(format, token)
    );
}

function runExport(format, token) {
    /* The sequence the weight was measured on — rebuilt from the same state
     * below, so a motion GIF already encoded for it is this export's file. */
    const measured = seq;
    rebuildSequence();
    const items = seq;
    { const rh = $('ptg-ram-hint'); if (rh) rh.classList.add('hidden'); }
    const durMs = items.reduce((s, i) => s + i.delay, 0);

    exportAbort = new AbortController();
    /* Called again on purpose: setBusy decides whether to show Cancel from
     * exportAbort, which only exists now. The first call (before the font
     * wait) put the page in the busy state; this one reveals Cancel. */
    setBusy(true, t('encoding', 'Building the GIF…'));

    const fill = $('ptg-progress-fill');
    const started = performance.now();
    const opts = {
        width: out.width,
        height: out.height,
        background: background || '#ffffff',
        signal: exportAbort.signal,
        onProgress: (v) => { if (fill) fill.style.width = Math.round(v * 100) + '%'; },
    };

    /* GIF-specific options come SECOND: Object.assign lets later sources win,
     * and `opts.background` is the WebM fallback white. Assigning them first
     * would let that white overwrite the transparent choice, so a transparent
     * background silently exported as a white one. */
    const moving = animatedStill();
    const reuse = moving && format === 'gif' && motionDone && motionDone.seq === measured && motionDone.key === motionKey()
        ? motionDone.blob : null;
    const job = format === 'webm'
        ? encodeWebm(items, opts)
        : reuse ? Promise.resolve(reuse)
        : moving ? encodeMotionGif(items, exportAbort.signal, opts.onProgress)
        : encodeGif(items, Object.assign({}, opts, {
            colors: colors(),
            loop: loopValue(),
            dither: dithering(),
            globalPalette: globalPalette(),
            /* null keeps GIF's 1-bit transparency; a colour flattens onto it. */
            background: background || null,
        }));

    job.then((blob) => {
        save(blob, format);
        if (token && token.commit) token.commit();
        exportedHere = true;
        stage('export_done');
        showResult(blob, format, items.length, durMs);
        ae('export_ok', Object.assign({
            fmt: format, frames: items.length, bytes: blob.size,
            w: out.width, h: out.height, fade: fadeMs(), dir: direction,
        }, motionLane() ? { motion: moving ? motion : 'none', overlay, speed: motionSpeed, reused: reuse ? 1 : 0 } : {}));
        try {
            window.__aeToolComplete && window.__aeToolComplete({
                format,
                frames: items.length,
                bytes: blob.size,
                ms: Math.round(performance.now() - started),
            });
        } catch (_) {}
        try { window.__toast && window.__toast(t('export_done', 'Ready — check your downloads'), 'success'); } catch (_) {}
    }).catch((err) => {
        if (token && token.abort) token.abort();
        const cancelled = err && (err.name === 'AbortError' || String(err.message || err) === 'aborted');
        ae('export_fail', { fmt: format, cancelled: !!cancelled, err: String(err && err.message || err).slice(0, 80) });
        if (cancelled) {
            try { window.__toast && window.__toast(t('export_cancelled', 'Export cancelled'), 'info'); } catch (_) {}
            return;
        }
        if (err && err.code === 'gif_ram_budget') {
            setBusy(false);
            showRamHint(err);
            return;
        }
        /* Память кончилась при сборке — сырое «Data cannot be cloned» верно,
           но не говорит, ЧТО делать. Называем оба рычага: меньше кадров или
           меньше размер. */
        if (err && err.gifOutOfMemory) {
            try {
                window.__toast && window.__toast(
                    t('export_oom', 'Not enough memory for this GIF — use fewer frames or a smaller size'),
                    'error'
                );
            } catch (_) {}
            return;
        }
        try {
            window.__handleError
                ? window.__handleError(err, 'photo-to-gif:export', { say: t('export_fail', 'Export failed') })
                : (window.__toast && window.__toast(t('export_fail', 'Export failed'), 'error'));
        } catch (_) {}
    }).finally(() => {
        exportAbort = null;
        setBusy(false);
        if (fill) fill.style.width = '0%';
        renderAll({ list: false });
    });
}

/* ── wiring ────────────────────────────────────────────────────────────── */

function resetProject() {
    frames = [];
    sourceKeys = [];
    seq = [];
    motionDone = null;
    /* The frame and captions SURVIVE a photo clear — someone making a set of
     * cards keeps the design and swaps the picture. Only the thumbnail cache
     * key is dropped so the gallery redraws with the next photo. */
    gallerySrc = null;
    stopPlayback();
    partialWarned = false;
    const note = $('ptg-note');
    if (note) note.classList.add('hidden');
    const hint = $('ptg-max-hint');
    if (hint) hint.classList.add('hidden');
    closeResult();
    renderAll();
}

function closeResult() {
    const box = $('ptg-result');
    if (box) box.classList.add('hidden');
    if (resultUrl) { try { URL.revokeObjectURL(resultUrl); } catch (_) {} resultUrl = null; }
    const media = $('ptg-result-media');
    if (media) media.textContent = '';
}

export function mount() {
    const drop = $('ptgDrop');
    if (!drop) return;

    const fadeNote = $('ptg-fade-note');
    if (fadeNote) fadeNote.dataset.base = fadeNote.textContent;

    /* НЕ голый addEventListener: этот модуль грузится как ES-модуль, то есть
     * заведомо отложенно, и на медленном соединении пользователь успевает
     * выбрать файл ДО того, как обработчик встал. Событие тогда некому
     * услышать, и тул сидит мёртвым при выбранном файле. Общий биндер
     * доливает уже выбранный файл; он приезжает отложенным скриптом, поэтому
     * проверка typeof обязательна — без неё падает весь модуль. */
    const onFiles = (d) => addFiles(d.files && d.files.length ? d.files : (d.file ? [d.file] : []));
    if (typeof window.__bindUploaderFile === 'function') {
        window.__bindUploaderFile(drop, (f, d) => onFiles(d || { file: f }));
    } else {
        drop.addEventListener('tool-uploader:file', (e) => onFiles(e.detail || {}));
    }
    drop.addEventListener('tool-uploader:clear', resetProject);

    const fps = $('ptg-fps');
    if (fps) {
        fps.addEventListener('change', () => {
            const d = globalDelay();
            /* A frame the user gave an explicit duration keeps it — otherwise the
             * speed control would silently undo their per-frame edits. */
            frames.forEach((f) => { if (!f.pinned) f.delay = d; });
            ae('speed_set', { fps: fps.value });
            renderAll();
        });
    }

    ['ptg-fit', 'ptg-colors', 'ptg-fade', 'ptg-loop'].forEach((id) => {
        const el = $(id);
        if (el) el.addEventListener('change', () => {
            stopPlayback();
            ae('opt_set', { id, v: el.value });
            renderAll({ list: false });
        });
    });
    ['ptg-dither', 'ptg-palette'].forEach((id) => {
        const el = $(id);
        if (el) el.addEventListener('change', () => {
            ae('opt_set', { id, v: el.checked ? 1 : 0 });
            renderAll({ list: false });
        });
    });

    const motionSel = $('ptg-motion-sel');
    const overlaySel = $('ptg-overlay');
    const speedSel = $('ptg-motion-speed');
    const onMotion = () => {
        const was = playing;
        stopPlayback();
        renderAll({ list: false });
        if ((was || !reducedMotion()) && animatedStill()) startPlayback();
    };
    if (motionSel) {
        motionSel.value = motion;
        motionSel.addEventListener('change', () => { motion = motionSel.value; ae('motion_set', { m: motion }); onMotion(); });
    }
    if (overlaySel) {
        overlaySel.value = overlay;
        overlaySel.addEventListener('change', () => { overlay = overlaySel.value; ae('overlay_set', { o: overlay }); onMotion(); });
    }
    if (speedSel) {
        speedSel.value = motionSpeed;
        speedSel.addEventListener('change', () => { motionSpeed = speedSel.value; ae('motion_speed_set', { s: motionSpeed }); onMotion(); });
    }

    const dirBox = $('ptg-dir');
    if (dirBox) {
        dirBox.addEventListener('click', (e) => {
            const btn = e.target.closest ? e.target.closest('button[data-dir]') : null;
            if (!btn) return;
            direction = btn.dataset.dir;
            Array.prototype.forEach.call(dirBox.querySelectorAll('button[data-dir]'), (b) => {
                b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
            });
            stopPlayback();
            ae('direction_set', { dir: direction });
            renderAll({ list: false });
        });
    }

    const bgBox = $('ptg-bg');
    if (bgBox) {
        const markBg = (el) => {
            Array.prototype.forEach.call(bgBox.querySelectorAll('.ptg-sw[data-bg]'), (b) => {
                b.setAttribute('aria-pressed', b === el ? 'true' : 'false');
            });
        };
        bgBox.addEventListener('click', (e) => {
            const btn = e.target.closest ? e.target.closest('.ptg-sw[data-bg]') : null;
            if (!btn) return;
            background = btn.dataset.bg || null;
            markBg(btn);
            ae('bg_set', { bg: background || 'none' });
            renderAll({ list: false });
        });
        const custom = $('ptg-bg-custom');
        if (custom) {
            custom.addEventListener('input', () => {
                background = custom.value;
                markBg(null);
                renderAll({ list: false });
            });
            custom.addEventListener('change', () => ae('bg_set', { bg: 'custom' }));
        }
    }

    const lock = $('ptg-lock');
    if (lock) {
        lock.addEventListener('click', () => {
            lockRatio = !lockRatio;
            lock.setAttribute('aria-pressed', lockRatio ? 'true' : 'false');
            ae('lock_ratio', { on: lockRatio });
        });
    }

    const syncSize = (which) => {
        const el = $(which === 'w' ? 'ptg-width' : 'ptg-height');
        if (!el) return;
        el.addEventListener('change', () => {
            const v = Math.max(16, Math.min(1600, parseInt(el.value, 10) || 0));
            const even = Math.max(16, v - (v % 2));
            const ratio = out.width / out.height;
            if (which === 'w') {
                out.width = even;
                if (lockRatio) out.height = Math.max(16, Math.round(even / ratio) - (Math.round(even / ratio) % 2));
            } else {
                out.height = even;
                if (lockRatio) out.width = Math.max(16, Math.round(even * ratio) - (Math.round(even * ratio) % 2));
            }
            ae('size_set', { w: out.width, h: out.height });
            stopPlayback();
            renderAll({ list: false });
        });
    };
    syncSize('w');
    syncSize('h');

    const scrub = $('ptg-scrub');
    if (scrub) {
        scrub.addEventListener('input', () => {
            stopPlayback();
            renderPreview(parseInt(scrub.value, 10) || 0);
            renderPos();
        });
    }

    const play = $('ptg-play');
    if (play) play.addEventListener('click', () => (playing ? stopPlayback() : startPlayback()));

    const gif = $('ptg-export-gif');
    if (gif) gif.addEventListener('click', () => doExport('gif'));
    const webm = $('ptg-export-webm');
    if (webm) webm.addEventListener('click', () => doExport('webm'));

    const cancel = $('ptg-cancel');
    if (cancel) cancel.addEventListener('click', () => {
        if (exportAbort) { exportAbort.abort(); ae('export_cancel_click', {}); }
    });

    const sort = $('ptg-sort');
    if (sort) sort.addEventListener('click', () => {
        frames.sort((a, b) => naturalCompare(a.from, b.from));
        ae('frames_sorted', { n: frames.length });
        stopPlayback();
        renderAll();
        try { window.__toast && window.__toast(t('sorted', 'Sorted by file name'), 'success'); } catch (_) {}
    });

    const clear = $('ptg-clear');
    if (clear) clear.addEventListener('click', resetProject);

    const rClose = $('ptg-result-close');
    if (rClose) rClose.addEventListener('click', closeResult);

    /* ── card controls ─────────────────────────────────────────────────── */
    buildCats();

    const gallery = $('ptg-card-gallery');
    if (gallery) {
        gallery.addEventListener('click', (e) => {
            const b = e.target.closest ? e.target.closest('[data-frame]') : null;
            if (!b) return;
            card.setFrame(b.dataset.frame);
            markFrameSelection();
            stopPlayback();
            ae('card_frame', { id: b.dataset.frame });
            afterFonts(() => renderAll({ list: false }));
        });
    }

    const fontSel = $('ptg-card-font');
    if (fontSel) {
        card.fonts().forEach((f) => {
            const o = document.createElement('option');
            o.value = f.id;
            /* Display faces carry their own brand name; the three generic rows
             * do not, so those get a translated label. */
            o.textContent = f.family
                ? f.family.replace(/^PF\s+/, '')
                : t('card_font_' + f.id, f.id);
            fontSel.appendChild(o);
        });
        fontSel.value = card.style().font;
        fontSel.addEventListener('change', () => {
            card.setStyle({ font: fontSel.value });
            ae('card_font', { id: fontSel.value });
            card.preloadFont(fontSel.value).then(() => renderAll({ list: false }));
        });
    }

    ['top', 'bottom'].forEach((slot) => {
        const el = $('ptg-card-' + slot);
        if (!el) return;
        const apply = () => {
            card.setText(slot, el.value);
            stopPlayback();
            afterFonts(() => renderAll({ list: false }));
        };
        el.addEventListener('input', apply);
        el.addEventListener('change', () => ae('card_text', { slot, len: el.value.length }));
    });

    const colorEl = $('ptg-card-color');
    if (colorEl) {
        colorEl.addEventListener('input', () => {
            card.setStyle({ color: colorEl.value });
            renderAll({ list: false });
        });
    }
    const strokeEl = $('ptg-card-stroke');
    if (strokeEl) {
        strokeEl.addEventListener('change', () => {
            card.setStyle({ stroke: strokeEl.checked ? 0.14 : 0 });
            ae('card_stroke', { on: strokeEl.checked });
            renderAll({ list: false });
        });
    }

    initFunnel(SLUG);
    stage('ready');
    renderAll();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount, { once: true });
} else {
    mount();
}
